import { useCallback, useEffect, useRef } from 'react';
import { useAbility } from '@casl/react';
import { captureException } from '@sentry/react';

import { API_BASE_URL } from '@/constants';
import { AbilityContext } from '@/contexts';
import { userService } from '@/services/database';
import { emitResourceActivityDeltaPacket } from '@/shared/components/activity/resourceActivityEvents';
import { emitResourceCommentDeltaPacket } from '@/shared/components/activity/resourceCommentEvents';
import {
    ActivityPayload,
    DeltaPacket,
    InboxNotificationDeltaPacket,
    subscribeLocalDeltaPackets,
    useActivityStore,
    useInboxNotificationStore,
    usePresenceStore,
} from '@/stores';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/database/entities/00-RBAC/01-Users';
import { UserDeltaPacket } from '@/stores/database/entities/00-RBAC/01-Users/types';
import { useWorkspaceStore } from '@/stores/database/entities/00-RBAC/02-Workspaces';
import { WorkspaceDeltaPacket } from '@/stores/database/entities/00-RBAC/02-Workspaces/types';
import { useMemberStore } from '@/stores/database/entities/00-RBAC/03-Members';
import { MemberDeltaPacket } from '@/stores/database/entities/00-RBAC/03-Members/types';
import { useFolderStore } from '@/stores/database/entities/01-Folders';
import { FolderDeltaPacket } from '@/stores/database/entities/01-Folders/types';
import { useInvestmentStore } from '@/stores/database/entities/02-Investments';
import { InvestmentDeltaPacket } from '@/stores/database/entities/02-Investments/types';
import { useMemoStore } from '@/stores/database/entities/04-Memos';
import { MemoDeltaPacket } from '@/stores/database/entities/04-Memos/types';
import { usePipelineStore } from '@/stores/database/entities/05-Pipelines';
import { PipelineDeltaPacket } from '@/stores/database/entities/05-Pipelines/types';
import { useDashboardStore } from '@/stores/database/entities/99-Relations/01-Dashboards';
import { DashboardDeltaPacket } from '@/stores/database/entities/99-Relations/01-Dashboards/types';
import { useReportStore } from '@/stores/database/entities/99-Relations/02-Reports';
import { ReportDeltaPacket } from '@/stores/database/entities/99-Relations/02-Reports/types';
import { useListStore } from '@/stores/database/entities/99-Relations/03-Lists';
import { ListDeltaPacket } from '@/stores/database/entities/99-Relations/03-Lists/types';
import { useListViewColumnStore } from '@/stores/database/entities/99-Relations/99-ListViewColumns';
import { ListViewColumnDeltaPacket } from '@/stores/database/entities/99-Relations/99-ListViewColumns/types';
import { useListViewStore } from '@/stores/database/entities/99-Relations/99-ListViews';
import { ListViewDeltaPacket } from '@/stores/database/entities/99-Relations/99-ListViews/types';
import { useReportComponentStore } from '@/stores/database/entities/99-Relations/99-ReportComponents';
import { ReportComponentDeltaPacket } from '@/stores/database/entities/99-Relations/99-ReportComponents/types';
import { ResourceActivity, ResourceComment } from '@/types';

type WSMessage =
    | { v: 1; type: 'presence:heartbeat'; status: 'online' | 'away' }
    | {
          v: 1;
          type: 'presence:update';
          userId: string;
          status: 'online' | 'away' | 'offline';
          lastSeenAt: number | null;
      }
    | { v: 1; type: 'activity:update'; userId: string; payload: ActivityPayload }
    | { v: 1; type: 'activity:set'; resourceType: string; resourceId: string }
    | { v: 1; type: 'activity:clear' }
    | InvestmentDeltaPacket
    | FolderDeltaPacket
    | MemoDeltaPacket
    | PipelineDeltaPacket
    | DashboardDeltaPacket
    | ListDeltaPacket
    | ReportDeltaPacket
    | ReportComponentDeltaPacket
    | ListViewDeltaPacket
    | ListViewColumnDeltaPacket
    | UserDeltaPacket
    | WorkspaceDeltaPacket
    | MemberDeltaPacket
    | InboxNotificationDeltaPacket
    | DeltaPacket<'collaborator'>
    | DeltaPacket<'resourceActivity', ResourceActivity>
    | DeltaPacket<'resourceComment', ResourceComment>
    | { v: 1; type: 'auth:logout'; userId: string; sessionId?: string; timestamp: number };

export function sendWhenOpen(ws: WebSocket, msg: WSMessage) {
    if (!ws) {
        return;
    }

    const send = () => ws.send(JSON.stringify(msg));

    if (ws.readyState === WebSocket.OPEN) {
        send();
    } else {
        const listener = () => {
            if (ws.readyState === WebSocket.OPEN) {
                send();
                ws.removeEventListener('open', listener);
            }
        };

        ws.addEventListener('open', listener);
    }
}

export const useWorkspaceWebSocket = (token: string) => {
    const ability = useAbility(AbilityContext);
    const updatePresence = usePresenceStore((s) => s.update);
    const updateActivity = useActivityStore((s) => s.update);
    const applyInvestmentDelta = useInvestmentStore((s) => s.applyInvestmentDelta);
    const applyFolderDelta = useFolderStore((s) => s.applyFolderDelta);
    const applyMemoDelta = useMemoStore((s) => s.applyMemoDelta);
    const applyPipelineDelta = usePipelineStore((s) => s.applyPipelineDelta);
    const applyDashboardDelta = useDashboardStore((s) => s.applyDashboardDelta);
    const applyListDelta = useListStore((s) => s.applyListDelta);
    const applyReportDelta = useReportStore((s) => s.applyReportDelta);
    const applyReportComponentDelta = useReportComponentStore((s) => s.applyReportComponentDelta);
    const applyListViewDelta = useListViewStore((s) => s.applyListViewDelta);
    const applyListViewColumnDelta = useListViewColumnStore((s) => s.applyListViewColumnDelta);
    const applyUserDelta = useUserStore((s) => s.applyUserDelta);
    const clearSelectedUser = useUserStore((s) => s.clearSelectedUser);
    const applyWorkspaceDelta = useWorkspaceStore((s) => s.applyWorkspaceDelta);
    const clearSelectedWorkspace = useWorkspaceStore((s) => s.clearSelectedWorkspace);
    const selectedWorkspaceId = useWorkspaceStore((s) => s.selectedWorkspace?.id);
    const applyMemberDelta = useMemberStore((s) => s.applyMemberDelta);
    const clearSelectedMember = useMemberStore((s) => s.clearSelectedMember);
    const applyInboxDelta = useInboxNotificationStore((s) => s.applyInboxDelta);

    const wsRef = useRef<WebSocket | null>(null);
    const permissionRefreshRef = useRef<number | null>(null);

    const refreshPermissions = useCallback(() => {
        if (permissionRefreshRef.current) {
            window.clearTimeout(permissionRefreshRef.current);
        }

        permissionRefreshRef.current = window.setTimeout(async () => {
            try {
                const permissions = await userService.getPermissions();

                ability.update(permissions.rules);
            } catch (error) {
                captureException(error);
            } finally {
                permissionRefreshRef.current = null;
            }
        }, 150);
    }, [ability]);

    const handleMessage = useCallback(
        (msg: WSMessage) => {
            if (msg.type === 'presence:update') {
                updatePresence(msg.userId, { status: msg.status, lastSeenAt: msg.lastSeenAt ?? Date.now() });
            }

            if (msg.type === 'activity:update') {
                updateActivity(msg.userId, msg.payload);
            }

            if (msg.type === 'delta:packet') {
                if (
                    'workspaceId' in msg &&
                    msg.workspaceId &&
                    selectedWorkspaceId &&
                    msg.workspaceId !== selectedWorkspaceId
                ) {
                    return;
                }

                switch (msg.entity) {
                    case 'investment':
                        applyInvestmentDelta(msg);
                        if (msg.operation === 'delete' || msg.payload?.visibility) {
                            refreshPermissions();
                        }
                        break;
                    case 'folder':
                        applyFolderDelta(msg);
                        break;
                    case 'memo':
                        applyMemoDelta(msg);
                        break;
                    case 'pipeline':
                        applyPipelineDelta(msg);
                        break;
                    case 'dashboard':
                        applyDashboardDelta(msg);
                        if (msg.operation === 'create' || msg.operation === 'delete') {
                            refreshPermissions();
                        }
                        break;
                    case 'list':
                        applyListDelta(msg);
                        break;
                    case 'report':
                        applyReportDelta(msg);
                        if (msg.operation === 'create' || msg.operation === 'delete') {
                            refreshPermissions();
                        }
                        break;
                    case 'reportComponent':
                        applyReportComponentDelta(msg);
                        break;
                    case 'listView':
                        applyListViewDelta(msg);
                        break;
                    case 'listViewColumn':
                        applyListViewColumnDelta(msg);
                        break;
                    case 'user':
                        applyUserDelta(msg);
                        break;
                    case 'workspace':
                        applyWorkspaceDelta(msg);
                        break;
                    case 'member':
                        applyMemberDelta(msg);
                        refreshPermissions();
                        break;
                    case 'inbox':
                        applyInboxDelta(msg);
                        break;
                    case 'collaborator':
                        refreshPermissions();
                        break;
                    case 'resourceActivity':
                        emitResourceActivityDeltaPacket(msg);
                        break;
                    case 'resourceComment':
                        emitResourceCommentDeltaPacket(msg);
                        break;
                }
            }

            if (msg.type === 'auth:logout') {
                wsRef.current?.close(4001, 'Authentication session revoked');
                useAuthStore.getState().setAccessToken(null);
                clearSelectedUser();
                clearSelectedWorkspace();
                clearSelectedMember();
                localStorage.removeItem('token');

                if (window.location.pathname !== '/auth/logout') {
                    window.location.replace('/auth/logout');
                }
            }
        },
        [
            updatePresence,
            updateActivity,
            applyInvestmentDelta,
            applyFolderDelta,
            applyMemoDelta,
            applyPipelineDelta,
            applyDashboardDelta,
            applyListDelta,
            applyReportDelta,
            applyReportComponentDelta,
            applyListViewDelta,
            applyListViewColumnDelta,
            applyUserDelta,
            applyWorkspaceDelta,
            applyMemberDelta,
            applyInboxDelta,
            refreshPermissions,
            selectedWorkspaceId,
            clearSelectedUser,
            clearSelectedWorkspace,
            clearSelectedMember,
        ],
    );

    useEffect(() => {
        return subscribeLocalDeltaPackets((packet) => {
            handleMessage(packet as WSMessage);
        });
    }, [handleMessage]);

    useEffect(() => {
        if (!token || wsRef.current) {
            return;
        }

        const protocol = import.meta.env.DEV ? 'ws' : 'wss';
        const ws = new WebSocket(`${protocol}://${API_BASE_URL}/ws`, [token]);

        wsRef.current = ws;

        ws.onopen = () => {
            sendWhenOpen(ws, {
                v: 1,
                type: 'presence:heartbeat',
                status: 'online',
            });
        };

        ws.onmessage = (e) => {
            try {
                const msg = JSON.parse(e.data) as WSMessage;

                handleMessage(msg);
            } catch (err) {
                console.error('WS parse error', err);
            }
        };

        ws.onclose = () => {
            wsRef.current = null;
        };

        return () => {
            if (permissionRefreshRef.current) {
                window.clearTimeout(permissionRefreshRef.current);
                permissionRefreshRef.current = null;
            }

            if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
                ws.close(1000, 'Client unmounted');
            }
        };
    }, [token, handleMessage]);

    return wsRef;
};
